import { useEffect, useState } from "react";
import { storyblokEditable } from "@storyblok/react/rsc";
import { useMembership } from "@/lib/hooks/useMembership";

import type {
  AdSpaceStoryblok,
  AdStoryblok,
  CampaignStoryblok,
} from "@/component-types-sb";
import Ad from "./Ad";
import { fetchStoryblokList } from "@/lib/storyblok/fetchList";
import { randomItem } from "@/lib/utils/array";
import { cn } from "@/lib/utils";

type AdSpaceProps = {
  blok: AdSpaceStoryblok;
};

const AdSpace = ({ blok }: AdSpaceProps) => {
  const { isMember } = useMembership();

  const [ad, setAd] = useState<AdStoryblok | undefined>(
    blok.ad as AdStoryblok | undefined
  );
  const [campaign, setCampaign] = useState<CampaignStoryblok>();
  const [isVisible, setIsVisible] = useState(false);

  const adSpace = blok.ad_space as string | "";
  const inSidebar = adSpace === "blog_sidebar";

  // Show sidebar ad only after scrolling past the table of contents
  useEffect(() => {
    if (isMember) return;

    const handleScroll = () => {
      if (!inSidebar) {
        setIsVisible(true);
        return;
      }

      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const distanceFromBottom =
        documentHeight - (scrollPosition + windowHeight);
      const toc = document.getElementById("toc")?.offsetHeight || 104;
      setIsVisible(scrollPosition >= toc + 100 && distanceFromBottom > 800);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [inSidebar, isMember]);

  // Ueber den gecachten Proxy statt ueber die "use server"-Action.
  //
  // Bewusst NICHT auf Clerk warten: clerk-js braucht 1,7–2,2 s (siehe
  // app/layout.tsx), laedt auf iOS 14 gar nicht (private Klassenfelder) und
  // faellt bei Blockern oder Clerk-Stoerungen aus — die Anzeige kaeme dann
  // spaet oder nie. Fuer Mitglieder laeuft der Request ggf. einmal an und
  // wird beim Wechsel von `isMember` abgebrochen; er trifft den gecachten
  // Proxy und kostet keinen Storyblok-Request.
  useEffect(() => {
    if (isMember || blok.ad) return;

    const controller = new AbortController();

    fetchStoryblokList<CampaignStoryblok>(
      {
        content_type: "campaign",
        per_page: 100,
        resolve_relations: "campaign.ads",
        filter_query: {
          ad_spaces: { in: adSpace },
          disabled: { is: false },
        },
      },
      controller.signal,
    )
      .then((campaigns) => {
        if (!campaigns?.length) return;

        const campaign = randomItem(campaigns);
        setCampaign(campaign);

        const ads = campaign.content?.ads.filter((ad: AdStoryblok) =>
          ad.content?.ad_spaces?.includes(adSpace as string)
        );
        setAd(randomItem(ads));
      })
      .catch((err) => {
        if (err?.name !== "AbortError") console.error(err);
      });

    return () => controller.abort();
  }, [blok.ad, adSpace, isMember]);

  if (isMember) return null;

  return (
    <section
      {...storyblokEditable(blok)}
      data-ad-space-name={adSpace}
      data-campaign-name={campaign?.name}
      data-ad-name={ad?.name}
      className={cn("opacity-100", {
        ["lg:fixed lg:max-w-96 transition-opacity duration-300"]:
          inSidebar,
        ["opacity-0 pointer-events-none"]: inSidebar && !isVisible,
      })}
      style={inSidebar ? { top: "calc(8rem + var(--banner-height, 0px))" } : undefined}
    >
      {ad?.content && (
        <Ad
          blok={ad}
          campaign={campaign}
          className="mb-8"
        />
      )}
    </section>
  );
};

export default AdSpace;
